import { shiftFinancialTotals } from "../../helpers/shiftFinancialTotals";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { shiftActions } from "../../sagas/shiftSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { Badge, Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { FaCalendarAlt, FaCashRegister, FaEye, FaFilter, FaMoneyBillWave, FaUndoAlt, FaUserClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { formatPrice } from "../../helpers/utility";
import { SHIFT_DETAIL_PAGE } from "../../routes/routeConstants";
import { useNavigate } from "react-router-dom";
import "./ShiftPage.css";

const periodOptions = [
  { value: null, label: "Tous" },
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois" },
];

const SHIFT_FILTERS_STORAGE_KEY = "matjarpos.shiftPage.filters";
const defaultFilters = {
  commercialId: null,
  status: "OPENED",
  period: "month",
};

const pad = (n) => n.toString().padStart(2, "0");
const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getPeriodRange = (period) => {
  if (!period) return { startDate: null, endDate: null };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "today") {
    return { startDate: formatDate(today), endDate: formatDate(today) };
  }

  if (period === "week") {
    const day = today.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  if (period === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: formatDate(start), endDate: formatDate(today) };
  }

  return { startDate: null, endDate: null };
};

const getInitialFilters = () => {
  if (typeof window === "undefined") return defaultFilters;

  try {
    const savedFilters = window.sessionStorage.getItem(SHIFT_FILTERS_STORAGE_KEY);
    return savedFilters ? { ...defaultFilters, ...JSON.parse(savedFilters) } : defaultFilters;
  } catch (error) {
    return defaultFilters;
  }
};

const getNetSalesAmount = shift => shiftFinancialTotals(shift).sales;
const getNetPaymentsAmount = shift => shiftFinancialTotals(shift).payments;

const ShiftPage = () => {
  const { t } = useTranslation();
  const { list: shifts = [], loading: shiftLoading } = useSelector((state) => state.shift);
  const { list: commercialList } = useSelector((state) => state.commercials);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(getInitialFilters);

  useEffect(() => {
    dispatch(commercialActions.list({ query: "" }));
  }, [dispatch]);

  useEffect(() => {
    const { period, ...restFilters } = filters;
    dispatch(shiftActions.fetchList({ ...restFilters, ...getPeriodRange(period) }));
  }, [dispatch, filters]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SHIFT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      // Storage can be unavailable in restricted browser contexts.
    }
  }, [filters]);

  const sortedShifts = useMemo(() => {
    return [...shifts].sort((a, b) => {
      const bTime = new Date(b.openingTime || b.createdAt || 0).getTime();
      const aTime = new Date(a.openingTime || a.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [shifts]);

  const totals = useMemo(() => {
    return sortedShifts.reduce(
      (acc, shift) => {
        acc.salesAfterTicketReturns += getNetSalesAmount(shift);
        acc.returns += shiftFinancialTotals(shift).returns;
        acc.noTicketReturns += shiftFinancialTotals(shift).noTicketReturns;
        acc.payments += getNetPaymentsAmount(shift);
        const outstanding = shiftFinancialTotals(shift).clientOutstanding;
        acc.clientOutstanding = acc.clientOutstanding == null || outstanding == null ? null : acc.clientOutstanding + outstanding;
        return acc;
      },
      { salesAfterTicketReturns: 0, returns: 0, noTicketReturns: 0, payments: 0, clientOutstanding: 0 }
    );
  }, [sortedShifts]);

  const commercialOptionsWithAll = [
    { value: null, label: t("common.all", "Tous") },
    ...(commercialList || []).map((com) => ({ value: com.id, label: com.fullName })),
  ];

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const periodLabel = periodOptions.find((option) => option.value === filters.period)?.label;
  const subtitle = filters.status === "OPENED"
    ? `${sortedShifts.length} sessions de caisse ouvertes${filters.period ? ` · ${periodLabel}` : ""}`
    : `${sortedShifts.length} sessions de caisse${filters.period ? ` · ${periodLabel}` : ""}`;

  const openDetails = (shift) => {
    navigate(SHIFT_DETAIL_PAGE, {
      state: {
        editMode: true,
        shift,
      },
    });
  };

  const statusBadge = (status) => {
    if (status === "OPENED") return <Badge color="warning" pill>En cours</Badge>;
    if (status === "CLOSED") return <Badge color="secondary" pill>À vérifier</Badge>;
    if (status === "VALIDATED") return <Badge color="success" pill>Vérifiée</Badge>;
    return <Badge color="secondary" pill>{status || "-"}</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
  };

  return (
    <Container fluid className="shift-page">
      <div className="shift-header">
        <div>
          <div className="shift-title">
            <FaUserClock />
            Sessions de caisse
          </div>
          <div className="shift-subtitle">{subtitle}</div>
          <p className="shift-help mb-0">Choisissez une session pour voir les ventes, les retours et vérifier la caisse.</p>
        </div>
      </div>

      <Row className="g-2 mb-2">
        <Col sm={6} xl={3}>
          <div className="shift-kpi">
            <FaCashRegister className="text-primary" />
            <span>Ventes après retours</span>
            <strong>{formatPrice(totals.salesAfterTicketReturns)}</strong>
          </div>
        </Col>
        <Col sm={6} xl={3}>
          <div className="shift-kpi">
            <FaUndoAlt className="text-warning" />
            <span>Articles retournés — tous les retours</span>
            <strong>{formatPrice(totals.returns)}</strong>
            <small>Sans ticket : {formatPrice(totals.noTicketReturns)}</small>
          </div>
        </Col>
        <Col sm={6} xl={3}>
          <div className="shift-kpi">
            <FaMoneyBillWave className="text-success" />
            <span>Argent reçu après remboursements</span>
            <strong>{formatPrice(totals.payments)}</strong>
          </div>
        </Col>
        <Col sm={6} xl={3}>
          <div className="shift-kpi">
            <FaUserClock className="text-warning" />
            <span>Crédit client — reste impayé</span>
            <strong>{totals.clientOutstanding == null ? "—" : formatPrice(totals.clientOutstanding)}</strong>
          </div>
        </Col>
      </Row>

      <Card className="shift-filter-card">
        <CardBody>
          <Row className="g-2 align-items-end">
            <Col xl={3} md={6}>
              <FormGroup className="mb-0">
                <Label for="shift-cashier" className="shift-label">Caissier</Label>
                <Select
                  inputId="shift-cashier"
                  classNamePrefix="shift-cashier"
                  options={commercialOptionsWithAll}
                  value={commercialOptionsWithAll.find((option) => option.value === filters.commercialId)}
                  onChange={(selectedOption) => setFilters((f) => ({ ...f, commercialId: selectedOption ? selectedOption.value : null }))}
                  placeholder={t("shifts.select")}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </FormGroup>
            </Col>
            <Col xl={3} md={4} sm={6}>
              <FormGroup className="mb-0">
                <Label for="shift-status" className="shift-label">État de la caisse</Label>
                <Input id="shift-status" type="select" value={filters.status || ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || null }))}>
                  <option value="">{t("common.all", "Tous")}</option>
                  <option value="OPENED">En cours</option>
                  <option value="CLOSED">À vérifier</option>
                  <option value="VALIDATED">Vérifiée</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xl={4} md={8} sm={12}>
              <FormGroup className="mb-0">
                <Label className="shift-label">Période</Label>
                <div className="shift-period-filter">
                  {periodOptions.map((option) => (
                    <Button
                      key={option.value || "all"}
                      color={filters.period === option.value ? "primary" : "light"}
                      aria-pressed={filters.period === option.value}
                      onClick={() => setFilters((f) => ({ ...f, period: option.value }))}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </FormGroup>
            </Col>
            <Col xl={2} md={6}>
              <Button color="light" className="shift-reset-btn" onClick={clearFilters} title={t("common.clearFilters")}>
                <FaFilter /> Réinitialiser
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="shift-list-card">
        <CardBody className="p-0">
          {shiftLoading ? (
            <div className="shift-empty">
              <Spinner color="primary" />
              <div>{t("common.loading")}</div>
            </div>
          ) : sortedShifts.length === 0 ? (
            <div className="shift-empty">Aucune session trouvée. Essayez une autre période ou un autre état.</div>
          ) : (
            <div className="shift-list">
              {sortedShifts.map((shift) => {
                const netSalesAmount = getNetSalesAmount(shift);
                const netPaymentsAmount = getNetPaymentsAmount(shift);
                return (
                  <div className="shift-row" key={shift.id}>
                    <div className="shift-row-main">
                      <div className="shift-row-title">
                        <strong>{shift.shiftCode}</strong>
                        {statusBadge(shift.status)}
                      </div>
                      <div className="shift-row-meta">
                        <span><FaUserClock /> {shift.commercialName || "-"}</span>
                        <span><FaCalendarAlt /> Début : {formatDateTime(shift.openingTime)}</span>
                        {shift.closingTime && <span>Fin : {formatDateTime(shift.closingTime)}</span>}
                      </div>
                    </div>

                    <div className="shift-row-amounts">
                      <div>
                        <span>Ventes après retours</span>
                        <strong>{formatPrice(netSalesAmount, false)} DH</strong>
                      </div>
                      <div>
                        <span>Articles retournés</span>
                        <strong>{formatPrice(shiftFinancialTotals(shift).returns, false)} DH</strong>
                        <small>Sans ticket : {formatPrice(shiftFinancialTotals(shift).noTicketReturns, false)} DH</small>
                      </div>
                      <div>
                        <span><FaMoneyBillWave /> Argent reçu</span>
                        <strong>{formatPrice(netPaymentsAmount, false)} DH</strong>
                      </div>
                      <div>
                        <span>Crédit client — reste impayé</span>
                        <strong>{shiftFinancialTotals(shift).clientOutstanding == null ? "—" : formatPrice(shiftFinancialTotals(shift).clientOutstanding)}</strong>
                      </div>
                    </div>

                    <Button color="primary" className="shift-action-btn" onClick={() => openDetails(shift)} title={t("shifts.consult")}>
                      <FaEye /> Voir les détails
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ShiftPage;
